import {
  BRAND_FILTER_QUERY_KEY,
  REGION_FILTER_QUERY_KEY,
} from "staticConfiguration";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box, { type BoxProps } from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { QueryParamMultiSelect } from "core/components/QueryComponents/QueryParamMultiselect";
import { QuerySortButton } from "core/components/QueryComponents/QuerySortButton";
import { QueryDateRangePicker } from "core/components/ReactHookFormComponents/QueryDateRangePicker";
import type { SelectOption } from "core/entities";
import { useBrandRegionOptions } from "core/hooks/useBrandRegionOptions";
import { useBreakpoints } from "core/hooks/useBreakpoints";
import {
  useNewsEventCategories,
  // useNewsEventsTags,
  useNewsEventsTypes,
} from "features/NewsEvents/hooks";
import { type Dispatch, type SetStateAction, useMemo } from "react";
import { useTranslation } from "react-i18next";

export const CATEGORY_FILTER_QUERY_KEY = "categories";
export const TAGS_FILTER_QUERY_KEY = "tags";
export const TYPES_FILTER_QUERY_KEY = "types";

export interface ArticleFiltersProps {
  expanded: boolean;
  setExpanded: Dispatch<SetStateAction<boolean>>;
  showDateRange: boolean;
  sx: BoxProps["sx"];
}

export function EventsArticlesFilters({
  expanded,
  setExpanded,
  showDateRange,
  sx,
}: ArticleFiltersProps) {
  const { t } = useTranslation("NewsEvents", {
    keyPrefix: "Next:NewsEvents:EventsArticlesFilters",
  });
  const {
    brandOptions,
    regionOptions,
    isLoading: isBrandRegionOptionsLoading,
  } = useBrandRegionOptions();

  const { isMobile } = useBreakpoints();
  const { isLoading, isError, data } = useNewsEventCategories();

  const areCategoriesReady = !isLoading && !isError && data;
  const categoryOptions = useMemo(() => {
    if (!Array.isArray(data?.items)) {
      return [];
    }
    return [
      ...data.items
        .map(({ id, title }) => ({ label: title, value: id }))
        .filter(
          (entry): entry is { label: string; value: string } =>
            typeof entry.label === "string" && typeof entry.value === "string"
        ),
      { label: t("uncategorized"), value: "uncategorized" },
    ];
  }, [data?.items, t]);

  // !TODO: re-enable when tags filtering is supported in the backend
  // const {
  //   isLoading: tagsLoading,
  //   isError: tagsError,
  //   data: tagsData,
  // } = useNewsEventsTags("events");

  const { data: typesData, isLoading: isTypesLoading } =
    useNewsEventsTypes("eventtype");

  const typeOptions: SelectOption[] =
    typesData?.items
      ?.map(({ id, title }) => ({ label: title, value: id }))
      .filter(
        (item): item is { label: string; value: string } =>
          typeof item.label === "string" && typeof item.value === "string"
      ) || [];

  // const areTagsReady =
  //   !tagsLoading && !tagsError && tagsData && Array.isArray(tagsData.items);
  // const tagOptions = areTagsReady
  //   ? (tagsData.items?.map((tagString) => ({
  //       label: tagString,
  //       value: tagString,
  //     })) ?? [])
  //   : [];
  const minWidth = "275px";

  const BrandRegionMultiSelects = (
    <>
      <QueryParamMultiSelect
        queryKey={BRAND_FILTER_QUERY_KEY}
        options={brandOptions}
        loading={isBrandRegionOptionsLoading}
        label={t("brands")}
      />
      <QueryParamMultiSelect
        queryKey={REGION_FILTER_QUERY_KEY}
        options={regionOptions}
        loading={isBrandRegionOptionsLoading}
        label={t("regions")}
      />
    </>
  );

  const CategoryTagsTypesMultiSelects = (
    <>
      <QueryParamMultiSelect
        queryKey={CATEGORY_FILTER_QUERY_KEY}
        options={categoryOptions}
        loading={!areCategoriesReady}
        label={t("categories")}
      />
      {/* <QueryParamMultiSelect
        queryKey={TAGS_FILTER_QUERY_KEY}
        options={tagOptions}
        loading={!areTagsReady}
        label={t("tags")}
      /> */}
      <QueryParamMultiSelect
        queryKey={TYPES_FILTER_QUERY_KEY}
        options={typeOptions}
        loading={isTypesLoading || !typesData}
        label={t("types")}
      />
    </>
  );

  const DateFilters = (
    <Collapse in={showDateRange}>
      <Stack direction="row" gap={2} flexWrap="wrap">
        <QueryDateRangePicker
          sx={{ flex: 1 }}
          fromQueryKey="startDateMin"
          fromLabel={t("startDateMin")}
          toQueryKey="startDateMax"
          toLabel={t("startDateMax")}
        />
        <Box flex={1} display="flex" minWidth={`min(${minWidth}, 100%)`}>
          <QuerySortButton
            variant="outlined"
            color="primary"
            sx={{
              flex: 1,
              height: "56px",
            }}
            queryKey="direction"
            defaultDirection="asc"
            ascendingLabel={t("sorting.asc")}
            descendingLabel={t("sorting.desc")}
          />
        </Box>
      </Stack>
    </Collapse>
  );

  if (isMobile) {
    return (
      <Box mb={4}>
        <Accordion
          disableGutters
          expanded={expanded}
          onChange={() => setExpanded((bool) => !bool)}
          sx={{
            border: (theme) => `1px solid ${theme.palette.grey[400]}`,
            backgroundColor: "background.default",
            color: "text.secondary",
          }}
          elevation={0}
        >
          <AccordionSummary
            sx={{ alignItems: "center" }}
            expandIcon={<ExpandMoreIcon />}
          >
            <FilterListIcon color="inherit" />{" "}
            <Typography sx={{ mx: 2 }}>{t("filters")}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack gap={2}>
              {BrandRegionMultiSelects}
              {CategoryTagsTypesMultiSelects}
              {DateFilters}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  }

  return (
    <Box sx={sx}>
      <Box
        sx={{
          marginX: "auto",
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
          "& > *": { flex: 1, minWidth },
        }}
      >
        {BrandRegionMultiSelects}
        {CategoryTagsTypesMultiSelects}
      </Box>
      {DateFilters}
    </Box>
  );
}
